/**
 *  @fileOverview Wizard logic handler
 *
 *  @author       Peter Schmiz <peter.schmiz@possible.com>
 *
 *  @requires     NPM:lodash
 */

import forEach from 'lodash/forEach';
import { customSelectController } from './custom-select-controller';
import { cookieHandling } from '../utils/cookie-handling';
import { objectFit } from '../utils/object-fit';
import camelCase from '../utils/camelcase';
import { trackEvent } from './analytics';

const PRE_JOINER = 'preJoiner';
const ACTIVE_HYBRID = 'activeHybrid';
const DEFERRED_HYBRID = 'deferredHybrid';
const ACTIVE_DC = 'activeDC';
const DEFERRED_DC = 'deferredDC';
const DEFERRED_DB = 'deferredDB';
const PENSIONER = 'pensioner';

const OPT_COOKIE = 'pensionOptout';

class WizardLogic {
  constructor() {
    this.currentStep = 0;
    this.choice = null;
    this.optOut = false;
    this.currentState = this.getInitialState();
    this.wizardDropdowns = [];
  }

  init() {
    this.initDOMElements();
    if (this.wizard !== null) {
      this.initBindings();
      this.setState();
      if (window.location && window.location.search && window.location.search.match(/\?member=new/)) {
        cookieHandling.eraseCookie(OPT_COOKIE);
      }
    }
  }

  initDOMElements() {
    this.wizard = document.body.querySelector('[data-wizard]');

    if (this.wizard !== null) {
      this.endStep = this.wizard.querySelector('[data-wizard-endstep]');
      this.cta = this.endStep.querySelector('[data-wizard-endstep-cta]');
      this.label = this.endStep.querySelector('[data-wizard-endstep-label]');
      this.optOutBlock = this.endStep.querySelector('[data-wizard-endstep-opt-out]');
      this.checkbox = this.optOutBlock.querySelector('input');

      this.wizardData = JSON.parse(this.wizard.getAttribute('data-wizard-data'));
      this.cookieName = this.wizard.getAttribute('data-wizard-cookie-key') || 'pensionTypeCookie';
      this.defaultLabel = this.wizard.getAttribute('data-wizard-default-label') || 'please select';

      forEach(this.wizard.querySelectorAll('[data-wizard-select]'), (item) => {
        this.wizardDropdowns.push({
          item,
          name: item.getAttribute('data-wizard-select'),
        });
      });
    }
  }

  initBindings() {
    forEach(this.wizardDropdowns, (item) => {
      const customSelect = customSelectController.getSelectByName(item.name);
      customSelect.setChangeCallback(this.onChange.bind(this));
    });

    this.checkbox.addEventListener('change', (e) => {
      this.optOut = e.currentTarget.checked;

      if (this.optOut) {
        cookieHandling.createCookie(OPT_COOKIE, 1, 30);
      } else {
        cookieHandling.eraseCookie(OPT_COOKIE);
      }

      this.setState();
      trackEvent({ eventCategory: 'content', eventAction: 'button click', eventContent: `option : ${this.optOut}` });
    }, false);

    if (this.cta) {
      this.cta.addEventListener('click', () => {
        if (this.choice !== null) {
          this.setIdAndUrl();
        }
      }, false);
    }
  }

  onChange(select, value) {
    const wizardSelect = select && camelCase(select.getAttribute('data-wizard-select').toLowerCase());

    if (wizardSelect === 'personType') {
      this.choice = null;
      this.resetSelects();
      this.resetState();
    }

    this.currentState[wizardSelect] = value;

    switch (wizardSelect) {
      case 'personType':
        this.currentStep = 1;
        break;
      case 'started':
        this.resetOptOut();
        this.currentStep = 2;
        break;
      case 'joinedV1':
      case 'joinedV2':
      case 'leaving':
        this.currentStep = 3;
        break;
      default:
        break;
    }

    this.setState();
    objectFit.update();
  }

  getInitialState() {
    return {
      personType: null,
      started: null,
      joinedV1: null,
      joinedV2: null,
      leaving: null,
    };
  }

  resetSelects() {
    forEach(this.wizardDropdowns, (item) => {
      const customSelect = customSelectController.getSelectByName(item.name);
      if (item.name !== 'person-type') {
        customSelect.resetSelection(this.defaultLabel);
      }
    });
  }

  resetOptOut() {
    this.checkbox.checked = false;
    this.optOut = false;
  }

  resetState() {
    this.currentState = this.getInitialState();
  }

  setState() {
    this.hideEndStep();

    this.hideSelect('person-type');
    this.hideSelect('started');
    this.hideSelect('joined-v1');
    this.hideSelect('joined-v2');
    this.hideSelect('leaving');

    switch (this.currentStep) {
      case 0:
        this.showSelect('person-type');
        break;
      case 1:
        this.showSelect('person-type');
        if (this.currentState.personType === 'thinking-about') {
          this.setEndState(PRE_JOINER);
        } else if (this.currentState.personType === 'pensioner') {
          this.setEndState(PENSIONER);
        } else if (
          this.currentState.personType === 'current-employee' ||
          this.currentState.personType === 'former-employee'
        ) {
          this.showSelect('started');
        }
        break;
      case 2:
        this.showSelect('person-type');
        this.showSelect('started');

        if (this.currentState.personType === 'current-employee') {
          if (this.currentState.started === 'before-july-1996') {
            this.showSelect('joined-v1');
          } else if (this.currentState.started === 'after-july-1996') {
            this.showSelect('joined-v2');
          }
        } else if (this.currentState.personType === 'former-employee') {
          if (this.currentState.started === 'before-july-1996') {
            this.showSelect('leaving');
          } else if (this.currentState.started === 'after-july-1996') {
            this.setEndState(DEFERRED_DC);
          }
        }
        break;
      case 3:
        this.showSelect('person-type');
        this.showSelect('started');
        if (this.currentState.personType === 'current-employee') {
          if (this.currentState.started === 'before-july-1996') {
            this.showSelect('joined-v1');
            if (this.currentState.joinedV1 === 'directly-from-hsbc') {
              if (this.optOut) {
                this.setEndState(DEFERRED_HYBRID);
              } else {
                this.setEndState(ACTIVE_HYBRID);
              }
            } else if (this.currentState.joinedV1 === 'from-first-district') {
              if (this.optOut) {
                this.setEndState(DEFERRED_DC);
              } else {
                this.setEndState(ACTIVE_DC);
              }
            }
          } else if (this.currentState.started === 'after-july-1996') {
            this.showSelect('joined-v2');
            if (this.currentState.joinedV2 === 'directly-from-hsbc-2') {
              if (this.optOut) {
                this.setEndState(DEFERRED_DC);
              } else {
                this.setEndState(ACTIVE_DC);
              }
            } else if (this.currentState.joinedV2 === 'from-m-and-s-bank') {
              if (this.optOut) {
                this.setEndState(DEFERRED_DB);
              } else {
                this.setEndState(ACTIVE_HYBRID);
              }
            }
          }
        } else if (this.currentState.personType === 'former-employee') {
          this.showSelect('leaving');
          if (this.currentState.leaving === 'before-30-june-2015') {
            this.setEndState(DEFERRED_DB);
          } else if (this.currentState.leaving === 'after-1-july-2015') {
            this.setEndState(DEFERRED_HYBRID);
          }
        }
        break;
      default:
        this.showSelect('person-type');
        break;
    }
  }

  hideSelect(name) {
    const select = this.getSelectByName(name);

    if (select) {
      select.classList.add('hidden');
    }
  }

  showSelect(name) {
    const select = this.getSelectByName(name);

    if (select) {
      select.classList.remove('hidden');
    }
  }

  setEndState(choice) {
    this.currentStep = 3;
    this.choice = choice;
    this.showOptOut();
    this.setEndStepInfo();
    this.showEndStep();
  }

  setEndStepInfo() {
    const profileInfo = this.getProfileInfo();

    this.setLabelInfo(profileInfo.WizardText);
    this.setCTA(profileInfo.WizardButtonTitle);

    switch (this.choice) {
      case PRE_JOINER:
        this.hideOptOut();
        break;
      case PENSIONER:
        this.hideOptOut();
        break;
      case ACTIVE_DC:
        break;
      case DEFERRED_DC:
        if (this.currentState.personType !== 'current-employee') {
          this.hideOptOut();
        }
        break;
      case DEFERRED_DB:
        if (this.currentState.personType !== 'current-employee') {
          this.hideOptOut();
        }
        break;
      case ACTIVE_HYBRID:
        break;
      case DEFERRED_HYBRID:
        if (this.currentState.personType !== 'current-employee') {
          this.hideOptOut();
        }
        break;
      default:
        break;
    }
  }

  setIdAndUrl() {
    const profileInfo = this.getProfileInfo();
    const id = profileInfo.ID;
    const url = profileInfo.Url;

    cookieHandling.eraseCookie(this.cookieName);
    cookieHandling.createCookie(this.cookieName, id, 30);
    window.location.href = url;
  }

  getProfileInfo() {
    let data = {
      WizardButtonTitle: 'Enter',
      WizardText: '<p></p>',
      Id: '',
      Url: '/',
    };

    if (this.wizardData) {
      data = this.getProfileByName(this.choice);
    }
    return data;
  }

  hideOptOut() {
    this.optOutBlock.classList.add('hidden');
  }

  showOptOut() {
    this.optOutBlock.classList.remove('hidden');
  }

  showEndStep() {
    this.endStep.classList.remove('hidden');
  }

  hideEndStep() {
    this.endStep.classList.add('hidden');
  }

  setCTA(label) {
    this.cta.setAttribute('data-event-content', label);
    this.cta.innerHTML = label && label !== '' ? label : 'Enter';
  }

  setLabelInfo(label) {
    this.label.innerHTML = label && label !== '' ? label : '';
  }

  getSelectByName(name) {
    const result = this.wizardDropdowns.filter(select => (select.name === name));

    if (result && result[0] && result[0].item) {
      return result[0].item;
    }
    return null;
  }

  getProfileByName(name) {
    const result = this.wizardData.filter(data => (Object.keys(data)[0] === name));

    if (result && result[0] && result[0][name]) {
      return result[0][name];
    }
    return null;
  }
}

export const wizardLogic = new WizardLogic();



// WEBPACK FOOTER //
// ./src/js/modules/wizard-logic.js